import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { defaultDebounceTime } from 'components/dropdown/shared';
import { DropdownInlineEditor } from 'components/inline_edit/DropdownInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useCorporation } from 'custom_hooks/data_hooks/corporation/useCorporation';
import {
    infiniteCorporationsDataSourceKey,
    useInfiniteCorporationDataSource,
} from 'custom_hooks/data_hooks/data_source/corporationInfiniteDataSource';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { LocationsTableCellProps } from 'pages/Settings/Locations/LocationsTable';
import { updateLocation } from 'utils/location';

export function LocationCorporationCell({ rowData, onChange }: LocationsTableCellProps) {
    const { canEditLocationDetails } = usePermissions();
    const { data: corporation } = useCorporation(rowData.Corporation.CorporationId);
    const {
        isLoading,
        hasNext,
        loadNext,
        results: corporations,
        filter,
        setFilter,
    } = useInfiniteCorporationDataSource({
        name: infiniteCorporationsDataSourceKey,
        defaultFilter: {},
    });

    const { showToast } = useNotificationContext();
    const { mutateAsync: updateCorporation, isPending: isSaving } = useMutation({
        mutationFn: (newCorporation: ICorporationResponse | undefined) =>
            updateLocation(rowData, {
                CorporationId: newCorporation?.Id,
            }),

        onSuccess: () => {
            onChange();
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location corporation',
            }),
    });

    // Need to use mutateAsync here to allow any failures to bubble up
    // to the inline editor
    const onConfirm = async (val: ICorporationResponse | undefined) => {
        await updateCorporation(val);
    };

    const setSearchText = useDebounceCallback(
        defaultDebounceTime,
        (searchText: string) => {
            setFilter((currentValue) => ({
                ...currentValue,
                nameSearch: searchText,
            }));
        }
    );

    return (
        <DropdownInlineEditor
            renderValue={(value) => value?.Name ?? 'Select a corporation'}
            value={corporation}
            options={{
                items: corporations,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dropdownConfig={{
                dataKey: (item) => item.Id,
                filterable: {
                    filterText: filter.nameSearch ?? '',
                    placeholder: 'Search corporations',
                    onChange: (value) => setSearchText(value),
                },
                renderItem: ({ item }) => item.Name,
                renderLabel: ({ value }) => value?.Name ?? 'Select a corporation',
                popup: { width: '50%' },
            }}
            onConfirm={onConfirm}
            onClose={() => setSearchText('')}
            isDisabled={!canEditLocationDetails || isSaving}
        />
    );
}
